/**
 * The palette, in TypeScript, because MUI cannot be handed a `var()`.
 *
 * MUI runs `getContrastRatio`, `alpha` and `lighten` over `palette.*` while the theme is being
 * constructed, so those entries have to be real colours. That is why this file exists and why it
 * is not a mistake: keeping real literals here is exactly what makes the ~27 `sx` call sites that
 * name a palette path (`'text.secondary'`, `'divider'`, `'background.paper'`) follow a theme
 * change with no edits at all.
 *
 * It is a mirror, not a source. `styles/` holds the palette; guardrail 5c compares this file
 * against it and fails on any value that disagrees, or any token named here that the stylesheet
 * does not define. Before that check existed, theme.ts carried 31 loose literals and `test/setup.ts`
 * carried a whole second copy of the palette, and nothing verified either of them.
 *
 * Add a token here only when MUI itself needs it. Everything else should read `var(--token)`.
 */
export type ThemeMode = 'light' | 'dark';

export const PALETTE: Record<ThemeMode, Record<string, string>> = {
  dark: {
    '--ink-950': '#0C0F14',
    '--ink-900': '#0E1218',
    '--ink-870': '#12171F',
    '--ink-850': '#141922',
    '--ink-800': '#171D28',
    '--ink-750': '#1C2331',
    '--ink-700': '#232C3C',
    '--ink-650': '#2C3648',
    '--line': '#28313F',
    '--line-soft': '#212A36',
    '--tx-hi': '#F3F6FB',
    '--tx-mid': '#A6B2C4',
    '--tx-low': '#8E99AB',
    '--tx-dim': '#68738A',
    '--field-border': '#6C7789',
    '--brand': '#0369A1',
    '--brand-hi': '#0272AE',
    '--brand-lo': '#024E79',
    '--brand-ai': '#6C63FF',
    '--on-brand': '#FFFFFF',
    '--on-accent': '#0C0F14',
    '--acc': '#38A3D9',
    '--acc-hi': '#5CB8E4',
    '--acc-tint': 'rgba(56, 163, 217, 0.10)',
    '--acc-line': 'rgba(56, 163, 217, 0.40)',
    '--teal': '#2FB3A8',
    '--teal-hi': '#279A90',
    '--teal-tint': 'rgba(47, 179, 168, 0.10)',
    '--teal-line': 'rgba(47, 179, 168, 0.40)',
    '--indigo': '#9B98F8',
    '--indigo-tint': 'rgba(155, 152, 248, 0.10)',
    '--indigo-line': 'rgba(155, 152, 248, 0.40)',
    '--gold': '#D99A4E',
    '--gold-tint': 'rgba(217, 154, 78, 0.10)',
    '--gold-line': 'rgba(217, 154, 78, 0.40)',
    '--green': '#4FB477',
    '--green-tint': 'rgba(79, 180, 119, 0.10)',
    '--green-line': 'rgba(79, 180, 119, 0.40)',
    '--amber': '#E0A54A',
    '--amber-tint': 'rgba(224, 165, 74, 0.10)',
    '--amber-line': 'rgba(224, 165, 74, 0.40)',
    '--red': '#EC8480',
    '--red-tint': 'rgba(236, 132, 128, 0.10)',
    '--red-line': 'rgba(236, 132, 128, 0.40)',
    '--grey': '#96A0B1',
    '--grey-tint': 'rgba(150, 160, 177, 0.10)',
    '--scrim': 'rgba(6, 9, 13, 0.72)',
    '--scrim-soft': 'rgba(6, 9, 13, 0.45)',
    '--paper': '#FFFFFF',
    '--paper-line': '#E0E0E0',
    '--paper-text': '#111111',
  },
  light: {
    '--ink-950': '#FFFFFF',
    '--ink-900': '#F4F7FA',
    '--ink-870': '#FFFFFF',
    '--ink-850': '#FAFBFD',
    '--ink-800': '#FFFFFF',
    '--ink-750': '#F1F5F9',
    '--ink-700': '#E6EDF5',
    '--ink-650': '#D8E1EC',
    '--line': '#DCE3EC',
    '--line-soft': '#EAEFF5',
    '--tx-hi': '#0F172A',
    '--tx-mid': '#44506A',
    '--tx-low': '#515D75',
    '--tx-dim': '#7C8698',
    '--field-border': '#6F7887',
    '--brand': '#0369A1',
    '--brand-hi': '#02537F',
    '--brand-lo': '#013D5E',
    '--brand-ai': '#4F46D6',
    '--on-brand': '#FFFFFF',
    '--on-accent': '#FFFFFF',
    '--acc': '#0369A1',
    '--acc-hi': '#024E79',
    '--acc-tint': 'rgba(3, 105, 161, 0.10)',
    '--acc-line': 'rgba(3, 105, 161, 0.40)',
    '--teal': '#0F6E66',
    '--teal-hi': '#0B5A54',
    '--teal-tint': 'rgba(15, 110, 102, 0.10)',
    '--teal-line': 'rgba(15, 110, 102, 0.40)',
    '--indigo': '#4338CA',
    '--indigo-tint': 'rgba(67, 56, 202, 0.10)',
    '--indigo-line': 'rgba(67, 56, 202, 0.40)',
    '--gold': '#8A5A2B',
    '--gold-tint': 'rgba(138, 90, 43, 0.10)',
    '--gold-line': 'rgba(138, 90, 43, 0.40)',
    '--green': '#0F6B31',
    '--green-tint': 'rgba(15, 107, 49, 0.10)',
    '--green-line': 'rgba(15, 107, 49, 0.40)',
    '--amber': '#7A5108',
    '--amber-tint': 'rgba(122, 81, 8, 0.10)',
    '--amber-line': 'rgba(122, 81, 8, 0.40)',
    '--red': '#B42318',
    '--red-tint': 'rgba(180, 35, 24, 0.10)',
    '--red-line': 'rgba(180, 35, 24, 0.40)',
    '--grey': '#52607A',
    '--grey-tint': 'rgba(82, 96, 122, 0.10)',
    '--scrim': 'rgba(15, 23, 42, 0.45)',
    '--scrim-soft': 'rgba(15, 23, 42, 0.22)',
    '--paper': '#FFFFFF',
    '--paper-line': '#E0E0E0',
    '--paper-text': '#111111',
  },
};
